import React from 'react';

import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

import type { RAsyncItemsRendererProps, ShowMoreButtonRendererProps } from '@/_components';
import { Button, FormField, RSyncMultiCombobox } from '@/_components';

import { QuickAddContainer } from './quick-add-container';
import { SortableItemsList } from './sortable-items-list';
import type { QuickAddItemBase, SyncQuickAddSectionProps } from './types';

import { NoDataMessage } from '../no-data-message';

export function SyncQuickAddSection<T extends QuickAddItemBase, TFormValues extends FieldValues = FieldValues>({
  options,
  items,
  sectionTitle,
  title,
  icon = '/images/icons/users.svg',
  emptyDataMessage,
  control,
  fieldName,
  isEditable = false,
  sortable = false,
  renderItem,
  customRenderItems,
  rightSlot,
  ...syncMultiProps
}: SyncQuickAddSectionProps<T, TFormValues>) {
  const renderSelectedQuickAdd: RAsyncItemsRendererProps<T> = React.useCallback(
    ({ values, removeItem }) => (
      <div className="flex flex-col">
        {values.length > 0 ? (
          values.map((item, index) => {
            if (!item) {
              console.error('Contact not matched in the map', item);
              return null;
            }
            return renderItem(item, { index, isEditable, removeItem });
          })
        ) : (
          <NoDataMessage message={emptyDataMessage} />
        )}
      </div>
    ),
    [isEditable, emptyDataMessage, renderItem]
  );

  const renderShowMoreButton: ShowMoreButtonRendererProps = React.useCallback(
    ({ onClick, text }) => (
      <Button size="sm" as="link" className="dark:text-primary mt-2.5" block onClick={onClick}>
        {text}
      </Button>
    ),
    []
  );

  return (
    <QuickAddContainer sectionTitle={sectionTitle} icon={icon} rightSlot={rightSlot}>
      <FormField
        control={control}
        name={fieldName}
        render={({ field }) => (
          <RSyncMultiCombobox<T>
            label={title}
            selectedItems={items}
            options={options}
            value={null}
            // value={items.length > 0 ? items[0] : ({ id: -1 } as T)}
            // controlShouldRenderValue={false}
            onValueChange={field.onChange}
            isEditable={isEditable}
            paginatedItems={5}
            renderItems={
              sortable
                ? ({ values, removeItem }) => (
                    <SortableItemsList
                      field={field as ControllerRenderProps}
                      values={values}
                      isEditable={isEditable}
                      emptyDataMessage={emptyDataMessage}
                      removeItem={removeItem}
                      renderItem={renderItem}
                    />
                  )
                : customRenderItems || renderSelectedQuickAdd
            }
            renderShowMoreButton={renderShowMoreButton}
            {...syncMultiProps}
          />
        )}
      />
    </QuickAddContainer>
  );
}
